import { AtSign, Globe, Phone } from "lucide-react";
import { type ReactNode, useMemo } from "react";
import { useTranslation } from "react-i18next";

import { ContactScope } from "@/api-v3";
import type { ContactRead } from "@/api-v3/model/contactRead";
import { Skeleton } from "@/components/ui/skeleton";
import { getCountryName } from "@/lib/country-name";

import { type Contact } from "./contact-list-types";

interface ContactsListProps {
  contacts: Contact[] | undefined;
  isLoading: boolean;
  userCountry?: string;
  dataTestId?: string;
  description?: ReactNode;
  variant?: "grid" | "list";
}

export function ContactsListComponent({
  contacts,
  isLoading,
  userCountry,
  dataTestId,
  description,
  variant = "grid",
}: ContactsListProps) {
  const { t } = useTranslation();
  const gridClassName =
    variant === "list"
      ? "grid grid-cols-1  md:grid-cols-2 gap-x-4 overflow-y-auto max-h-[60vh]"
      : "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-4";
  const { globalContacts, regionalContacts, localOfficeContacts } =
    useMemo(() => {
      if (contacts == null) {
        return {
          globalContacts: [],
          regionalContacts: [],
          localOfficeContacts: [],
        };
      }

      const globalContacts: Contact[] = [];
      const localOfficeContacts: Contact[] = [];
      const regionalContacts: Contact[] = [];

      for (const contact of contacts) {
        if (contact.scope === ContactScope.universal) {
          globalContacts.push(contact);
          continue;
        }

        if (contact.scope === ContactScope.site) {
          localOfficeContacts.push(contact);
          continue;
        }

        // If contact is within the same country as user then they are local
        if (
          contact.scope === ContactScope.country &&
          contact.country === userCountry
        ) {
          localOfficeContacts.push(contact);
          continue;
        }

        if (contact.scope === ContactScope.country) {
          regionalContacts.push(contact);
        }
      }
      return { globalContacts, regionalContacts, localOfficeContacts };
    }, [contacts, userCountry]);

  if (isLoading) {
    return <LoadingContactsList dataTestId={dataTestId} />;
  }

  return (
    <>
      {description != null && (
        <p data-testid={`${dataTestId}-description`} className="mb-2">
          {description}
        </p>
      )}
      <div className="divide-y divide-gray-50" data-testid={dataTestId}>
        {globalContacts.length > 0 && (
          <div className="py-2">
            <div className="py-2">
              <span className="text-[10px] font-bold uppercase tracking-widest text-gray-400">
                {t("global_contact")}
              </span>
            </div>
            <div className={gridClassName}>
              {globalContacts.map((contact) => (
                <div
                  key={contact.id}
                  className="flex items-center py-4 hover:bg-gray-50 transition-colors group"
                >
                  <div className="w-10 h-10 rounded-full bg-gray-900 flex items-center justify-center shrink-0">
                    <Globe className="w-5 h-5 text-white" />
                  </div>
                  <div className="ml-4 grow min-w-0">
                    <div className="text-sm font-bold text-gray-900 capitalize">
                      {contact.label}
                    </div>
                    <ContactLinkComponent contact={contact} />
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {regionalContacts.length > 0 && (
          <div className="py-2">
            <div className="py-2">
              <span className="text-[10px] font-bold uppercase tracking-widest text-gray-400">
                {t("regional_contact")}
              </span>
            </div>

            <div className={gridClassName}>
              {regionalContacts.map((contact) => (
                <div
                  key={contact.id}
                  className="flex items-center py-4 hover:bg-gray-50 transition-colors group"
                >
                  <div className="w-10 h-10 rounded-full bg-white border border-gray-200 flex items-center justify-center shrink-0 shadow-xs">
                    {contact.type === "phone" && (
                      <Phone className="w-5 h-5 text-gray-600" />
                    )}
                    {contact.type === "email" && (
                      <AtSign className="w-5 h-5 text-gray-600" />
                    )}
                  </div>
                  <div className="ml-4 grow min-w-0">
                    <div className="capitalize text-sm font-bold text-gray-900">
                      {contact.label}
                      {getCountryName(contact.country) != null &&
                        ` (${getCountryName(contact.country)})`}
                    </div>
                    <ContactLinkComponent contact={contact} />
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {localOfficeContacts.length > 0 && (
          <div className="py-2">
            <div className="py-2">
              <span className="text-[10px] font-bold uppercase tracking-widest text-gray-400">
                {t("local_contact")}
              </span>
            </div>

            <div className={gridClassName}>
              {localOfficeContacts.map((contact) => (
                <div key={contact.id} className="flex items-center py-4 group">
                  <div className=" w-10 h-10 rounded-full bg-white border border-gray-200 flex items-center justify-center shrink-0 shadow-xs">
                    {contact.type === "phone" && (
                      <Phone className="w-5 h-5 text-gray-600" />
                    )}
                    {contact.type === "email" && (
                      <AtSign className="w-5 h-5 text-gray-600" />
                    )}
                  </div>
                  <div className="ml-4 grow min-w-0">
                    <div className="text-sm font-bold text-gray-900 capitalize">
                      {contact.label}
                    </div>
                    <ContactLinkComponent contact={contact} />
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </>
  );
}

function ContactLinkComponent({ contact }: { contact: Contact }) {
  switch (contact.type) {
    case "email":
      return <EmailContactLinkComponent contact={contact} />;
    case "phone":
      return <PhoneContactLinkComponent contact={contact} />;
  }
}

function EmailContactLinkComponent({
  contact,
}: {
  contact: Omit<ContactRead, "type"> & { type: "email" };
}) {
  return (
    <div className=" text-gray-500">
      <a
        className="text-gray-500 hover:text-gray-700 text-sm underline break-all"
        href={`mailto:${contact.value}`}
        data-testid={`${contact.value}-contact-link-email`}
      >
        {contact.value}
      </a>
    </div>
  );
}

function PhoneContactLinkComponent({
  contact,
}: {
  contact: Omit<ContactRead, "type"> & { type: "phone" };
}) {
  return (
    <div className=" text-gray-500">
      <a
        className="text-gray-500 hover:text-gray-700 text-sm underline"
        href={`tel:${contact.value}`}
        data-testid={`${contact.label}-contact-link-phone`}
      >
        {contact.value}
      </a>
    </div>
  );
}

function LoadingContactsList({ dataTestId }: { dataTestId?: string }) {
  return (
    <div className="py-2" data-testid={`${dataTestId}-loading`}>
      <div className="py-2">
        <span className="text-[10px] font-bold uppercase tracking-widest text-gray-400">
          Global Hub
        </span>
      </div>
      <LoadingContact />
      <LoadingContact />
    </div>
  );
}

function LoadingContact() {
  return (
    <div className="flex items-center py-4">
      <Skeleton className="w-10 h-10 rounded-full shrink-0" />
      <div className="ml-4 grow space-y-2">
        <Skeleton className="h-4 w-2/5" />
        <Skeleton className="h-4 w-3/5" />
      </div>
    </div>
  );
}
